export const useVimeoProcessing = () => {
  const { getVimeoInfo } = useGeneral()
  
  // Helper function to convert player URL to API-compatible URL
  const getVimeoApiUrl = (url) => {
    if (!url) return null
    
    // Extract video ID and hash from various Vimeo URL formats
    let videoId = null
    let hash = null
    
    // Handle player.vimeo.com/video/ID?h=hash format
    if (url.includes('player.vimeo.com/video/')) {
      const match = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\?h=([^&]+))?/)
      if (match) {
        videoId = match[1]
        hash = match[2]
      }
    }
    // Handle vimeo.com/ID/hash format
    else if (url.includes('vimeo.com/')) {
      const match = url.match(/vimeo\.com\/(\d+)(?:\/([^/?]+))?/)
      if (match) {
        videoId = match[1]
        hash = match[2]
      }
    }
    
    // Return the API-compatible URL format with hash if present
    if (videoId) {
      return hash ? `https://vimeo.com/${videoId}/${hash}` : `https://vimeo.com/${videoId}`
    }
    
    return url
  }
  
  // Check if video is uploaded (has URLs) - can hide loader
  const isVideoUploaded = (videoInfo) => {
    return videoInfo?.url || videoInfo?.player_url
  }
  
  // Check if video is ready to view (has proper thumbnails) - can show button
  const isVideoReady = (videoInfo) => {
    // Must have both main_image and preview_image
    if (!videoInfo?.main_image || !videoInfo?.preview_image) {
      return false
    }
    
    // Check if thumbnails are still default placeholders (more strict patterns)
    const mainImage = videoInfo.main_image
    const previewImage = videoInfo.preview_image
    
    // Common Vimeo default thumbnail patterns
    const defaultPatterns = [
      'video/default',
      'default-',
      'default_',
      'placeholder',
      'processing',
      'thumbnail_default',
      'vimeo.com/filter/overlay',
      'i.vimeocdn.com/filter/overlay'
    ]
    
    // Check if either image contains default patterns
    for (const pattern of defaultPatterns) {
      if (mainImage.includes(pattern) || previewImage.includes(pattern)) {
        return false
      }
    }
    
    // Additional check: proper thumbnails should have specific dimensions or be from CDN
    const hasProperThumbnail = (
      mainImage.includes('i.vimeocdn.com') && 
      !mainImage.includes('filter/overlay') &&
      previewImage.includes('i.vimeocdn.com') &&
      !previewImage.includes('filter/overlay')
    )
    
    return hasProperThumbnail
  }
  
  // Legacy function for backward compatibility
  const isVideoProcessing = (videoInfo) => {
    return !isVideoReady(videoInfo)
  }
  
  // Helper function to get video info from nested data structure
  const getVideoInfo = (data, processedVideoInfo = null) => {
    if (processedVideoInfo) {
      return processedVideoInfo
    }
    
    if (data.video_info) {
      if (data.video_info.video_info) {
        return data.video_info.video_info
      } else {
        return data.video_info
      }
    }
    
    return null
  }

  // Helper function to get video URL with proper priority
  const getVideoUrl = (data, videoInfo) => {
    // Always prioritize video_message_link first
    if (data.video_message_link) {
      return data.video_message_link
    }
    
    // Fallback to video_info URLs
    if (videoInfo?.url || videoInfo?.player_url) {
      return videoInfo.url || videoInfo.player_url
    }
    
    return null
  }

  // Check if URL is a Vimeo video
  const isVimeoVideo = (url) => {
    return url && (url.includes('player.vimeo.com') || url.includes('vimeo.com'))
  }

  // Create media items array for gallery
  const createMediaItems = (data, processedVideoInfo = null) => {
    const items = []
    
    if (data.image_url) {
      items.push({ type: 'image', src: data.image_url })
    }
    
    const videoInfo = getVideoInfo(data, processedVideoInfo)
    const videoUrl = getVideoUrl(data, videoInfo)
    
    if (videoUrl) {
      if (isVimeoVideo(videoUrl)) {
        // Only add video to gallery if it's fully processed and ready
        if (isVideoReady(videoInfo)) {
          items.push({ type: 'vimeo', src: videoUrl })
        }
      } else {
        items.push({ type: 'video', src: videoUrl })
      }
    }
    
    return items
  }

  // Check video availability and determine button visibility
  const checkVideoAvailability = (data, processedVideoInfo = null) => {
    // Always show button if there's an image
    if (data.image_url) {
      return { shouldShow: true, needsValidation: false }
    }
    
    const videoInfo = getVideoInfo(data, processedVideoInfo)
    const videoUrl = getVideoUrl(data, videoInfo)
    
    if (!videoUrl) {
      return { shouldShow: false, needsValidation: false }
    }
    
    // For Vimeo videos, check if video is processed before showing
    if (isVimeoVideo(videoUrl)) {
      if (!isVideoReady(videoInfo)) {
        return { shouldShow: false, needsValidation: true, videoUrl, videoInfo }
      } else {
        return { shouldShow: true, needsValidation: false }
      }
    } else {
      // For non-Vimeo videos, show immediately
      return { shouldShow: true, needsValidation: false }
    }
  }
  
  // Simple polling mechanism for video processing
  const createVideoProcessor = (onVideoReady, onError) => {
    let vimeoInfoTick = ''
    
    const checkVideoInfo = (videoUrl, videoInfo) => {
      // If video is already ready, no need to poll
      if (isVideoReady(videoInfo)) {
        onVideoReady(videoInfo)
        return
      }
      
      // Poll every 20 seconds until video is ready
      clearInterval(vimeoInfoTick)
      vimeoInfoTick = setInterval(() => {
        const apiUrl = getVimeoApiUrl(videoUrl)
        
        getVimeoInfo({
          video_url: apiUrl
        }).then((res) => {
          if (res.data?.video_info) {
            const videoInfo = res.data.video_info
            
            if (isVideoReady(videoInfo)) {
              clearInterval(vimeoInfoTick)
              onVideoReady(videoInfo)
            }
          } else {
            clearInterval(vimeoInfoTick)
          }
        }).catch((err) => {
          if (err.error) {
            clearInterval(vimeoInfoTick)
            onError?.(err)
          }
        })
      }, 20000)
    }
    
    const cleanup = () => {
      if (vimeoInfoTick) clearInterval(vimeoInfoTick)
    }
    
    return {
      checkVideoInfo,
      cleanup
    }
  }
  
  return {
    getVimeoApiUrl,
    isVideoUploaded,
    isVideoReady,
    isVideoProcessing,
    getVideoInfo,
    getVideoUrl,
    isVimeoVideo,
    createMediaItems,
    checkVideoAvailability,
    createVideoProcessor
  }
}
